import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { ActorListEnum, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { actorForm2ApiMapper } from '../../../../Actor/mappers/actor.form2api.mapper';
import type { ActorForm_Actor } from '../../../../Actor/fragments/actor.fragment';
import type { ActorFormTab } from '../../../../Actor/enums/tab.enum';

import type { UpdateMutation } from '../types/props.type';

import { actorListKeyOfList } from '../utils/actor-list.util';

import { useFindActorIndexById } from './useFindIndexById.hook';

interface Params {
  readonly actorId?: Id | null;
  readonly folderBusinessEntityId: Id;
  readonly list: ActorListEnum;
  readonly directMutation?: UpdateMutation;
  readonly handleUpdateSuccess: () => void;
}

interface FormikArrayActions {
  readonly updateInForm: (
    index: number,
    value: Readonly<ActorForm_Actor>,
  ) => void;
}

export const useUpdateFolderBusinessEntityActorSubmit = ({
  actorId: pActorId,
  folderBusinessEntityId,
  list,
  directMutation,
  handleUpdateSuccess,
}: Readonly<Params>) => {
  const findIndexById = useFindActorIndexById(actorListKeyOfList(list));

  const handleSubmitFactory = useCallback(
    ({ updateInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<ActorForm_Actor, ActorFormTab>
      >) => {
        const { actorId } = ensureDefined({ actorId: pActorId });

        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(actorId);

          if (index === -1) {
            setWizardError({ messages: ["L'acteur n'existe pas"] });
            setSubmitting(false);
            return;
          }

          updateInForm(index, values);

          handleUpdateSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              actorableId: folderBusinessEntityId,
              list,
              actorId,
              update: actorForm2ApiMapper(values),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      directMutation,
      findIndexById,
      folderBusinessEntityId,
      handleUpdateSuccess,
      pActorId,
      list,
    ],
  );

  return { handleSubmitFactory };
};
